import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Navigation from './navigation.component';

const getDisplayName = WrappedComponent => (
  WrappedComponent.displayName
    || WrappedComponent.name
    || 'Component'
);

const pillDefinitionsIncludeActivePill = (pillDefinitions, pillType) => pillDefinitions
  .map(({ type }) => type)
  .includes(pillType);

const getPill = (getPillDefinitions, getActivePill, props) => {
  if (getActivePill && pillDefinitionsIncludeActivePill(
    getPillDefinitions(props),
    getActivePill(props)
  )) {
    return getActivePill(props);
  }

  if (getPillDefinitions(props)[0]) {
    return getPillDefinitions(props)[0].type;
  }

  return null;
};

const withPillNavigation = (getPillDefinitions, additionalProps = {}) => WrappedComponent => (
  class WithPillNavigation extends PureComponent {
    static displayName = `WithPillNavigation(${getDisplayName(WrappedComponent)})`;

    static propTypes = {
      elementToRenderBeforePills: PropTypes.node,
      elementToRenderNextToPills: PropTypes.node,
      isLoading: PropTypes.bool,
      ...additionalProps.propTypes,
    };

    static defaultProps = {
      elementToRenderBeforePills: null,
      elementToRenderNextToPills: null,
    };

    // using constructor to access props in getPillDefinitions
    constructor(props) {
      super(props);

      const activePill = getPill(
        getPillDefinitions,
        additionalProps.getActivePill,
        props
      );

      this.state = {
        activePill,
      };
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps(nextProps) {
      const prevActivePill = getPill(
        getPillDefinitions,
        additionalProps.getActivePill,
        this.props
      );
      const nextActivePill = getPill(
        getPillDefinitions,
        additionalProps.getActivePill,
        nextProps
      );

      if (prevActivePill !== nextActivePill) {
        const selectedPillDoesNotMatchNewPillDefinitions = this.state.activePill !== nextActivePill;

        if (selectedPillDoesNotMatchNewPillDefinitions) {
          this.setState({ activePill: nextActivePill }, () => {
            const onPillChangeEvent = this.props[additionalProps.onPillChangeEventPropKey];
            if (onPillChangeEvent) {
              onPillChangeEvent(nextActivePill);
            }
          });
        }
      }
    }

    changePill = pillType => {
      this.setState({ activePill: pillType }, () => {
        const onPillChangeEvent = this.props[additionalProps.onPillChangeEventPropKey];
        if (onPillChangeEvent) {
          onPillChangeEvent(pillType);
        }
      });
    };

    render() {
      const { elementToRenderBeforePills, elementToRenderNextToPills, isLoading } = this.props;
      const { activePill } = this.state;
      const classNames = classnames(
        `${getDisplayName(WrappedComponent).toLowerCase()}-with-pill-navigation`,
        additionalProps.className ? additionalProps.className : ''
      );

      const navigation = (
        <Navigation
          disabled={isLoading}
          activeElement={activePill}
          definitions={getPillDefinitions(this.props)}
          onChange={this.changePill}
        />
      );

      return (
        <div className={classNames}>
          {!elementToRenderBeforePills ? null : (
            <div className="row">
              <div className="col">
                {elementToRenderBeforePills}
              </div>
            </div>
          )}
          {!elementToRenderNextToPills ? navigation : (
            <div className="d-flex justify-content-between">
              {navigation}
              {elementToRenderNextToPills}
            </div>
          )}
          <WrappedComponent
            activePill={activePill}
            {...this.props}
          />
        </div>
      );
    }
  }
);

export default withPillNavigation;
